import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { ReferralBanner } from '@/types/referral';
import { Upload, Check } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface ReferralBannerUploadProps {
  banners: ReferralBanner[];
  onBannerUpdate: (banners: ReferralBanner[]) => void;
}

const ReferralBannerUpload: React.FC<ReferralBannerUploadProps> = ({
  banners,
  onBannerUpdate,
}) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState<string | null>(null);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, bannerId: string) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = async () => {
      URL.revokeObjectURL(objectUrl);
      
      if (img.width < 250 || img.height < 250) {
        toast({
          title: "Invalid image dimensions",
          description: "Image must be at least 250x250 pixels",
          variant: "destructive",
        });
        return;
      }

      setUploading(bannerId);
      
      try {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const updatedBanners = banners.map(banner => 
            banner.id === bannerId 
              ? { ...banner, imageUrl: reader.result as string }
              : banner
          );
          onBannerUpdate(updatedBanners);
          setUploading(null);
          
          toast({
            title: "Banner updated",
            description: "The referral banner has been updated successfully",
          });
        };
      } catch (error) {
        console.error('Error uploading image:', error);
        toast({
          title: "Upload failed",
          description: "Failed to upload the banner image",
          variant: "destructive",
        });
        setUploading(null);
      }
    };

    img.src = objectUrl;
  };

  const setDefaultBanner = (bannerId: string | null) => {
    const updatedBanners = banners.map(banner => ({
      ...banner,
      isDefault: banner.id === bannerId,
    }));
    onBannerUpdate(updatedBanners);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
        <div className="space-y-0.5">
          <Label className="text-sm font-medium">No Banner</Label>
          <p className="text-sm text-muted-foreground">
            Use links without any banner image
          </p>
        </div>
        <Switch
          checked={!banners.some(banner => banner.isDefault)}
          onCheckedChange={(checked) => checked ? setDefaultBanner(null) : null}
        />
      </div>

      <Separator className="my-4" />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {banners.map((banner) => (
          <Card key={banner.id} className={banner.isDefault ? 'border-primary' : ''}>
            <CardContent className="p-4 space-y-4">
              <div className="relative w-[250px] h-[250px] mx-auto bg-gray-100 rounded-lg overflow-hidden">
                {banner.imageUrl ? (
                  <img
                    src={banner.imageUrl}
                    alt={`Banner ${banner.name}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full text-gray-400">
                    250 x 250
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, banner.id)}
                  className="hidden"
                  id={`banner-upload-${banner.id}`}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute bottom-2 right-2"
                  disabled={uploading === banner.id}
                  onClick={() => document.getElementById(`banner-upload-${banner.id}`)?.click()}
                >
                  {uploading === banner.id ? (
                    "Uploading..."
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload
                    </>
                  )}
                </Button>
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor={`default-${banner.id}`} className="text-sm">
                  {banner.name || "Default Banner"}
                </Label>
                <Switch
                  id={`default-${banner.id}`}
                  checked={banner.isDefault}
                  onCheckedChange={() => setDefaultBanner(banner.id)}
                  disabled={banner.isDefault}
                />
              </div>
              
              {banner.isDefault && (
                <div className="flex items-center text-xs text-primary">
                  <Check className="h-3 w-3 mr-1" />
                  Default banner for this URL
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ReferralBannerUpload;
